import { useCallback } from 'react';

import { useAnnotationQueueRuns } from '@/hooks/useSwr';
import { AnnotationQueueRunSchema } from '@/types/schema';

// We fetch annotation queue runs in pages of size 3. When you reach index 2 of the current page, we fetch the next page (and we also fetch a page before the current)
export const useQueueRunWithPrefetch = (
  index: number,
  queue_id?: string,
  queue_size?: number,
  pageSize = 3
) => {
  const sharedOptions = { refreshInterval: 0, keepPreviousData: false };
  const currentPageIndex = Math.floor(index / pageSize);
  const currentPositionInPage = index % pageSize;

  const currPage = useAnnotationQueueRuns(
    queue_id,
    { limit: pageSize, offset: currentPageIndex * pageSize },
    sharedOptions
  );

  const previousPageOffset = (currentPageIndex - 1) * pageSize;
  const prevPage = useAnnotationQueueRuns(
    queue_id,
    currentPositionInPage <= 1 && previousPageOffset >= 0
      ? { limit: pageSize, offset: previousPageOffset }
      : null,
    sharedOptions
  );

  const nextPageOffset = (currentPageIndex + 1) * pageSize;
  const nextPage = useAnnotationQueueRuns(
    queue_id,
    currentPositionInPage >= 1 && (!queue_size || nextPageOffset < queue_size)
      ? { limit: pageSize, offset: nextPageOffset }
      : null,
    sharedOptions
  );

  // Returns true if we should block on the status mutation.
  const goToNextRun = useCallback(async () => {
    const currentPositionInPage = index % pageSize;
    const currentPageIndex = Math.floor(index / pageSize);
    const nextPageOffset = (currentPageIndex + 1) * pageSize;
    const shouldFetchNextPage =
      currentPositionInPage >= 1 &&
      (!queue_size || nextPageOffset < queue_size);

    if (index === (queue_size ?? 0) - 1) {
      // If we're at the end of the queue, we don't need to do anything unless we're at the beginning of the current page, in which case we need to make sure the previous page exists (or load it if not)
      if (
        currentPositionInPage === 0 &&
        previousPageOffset >= 0 &&
        !prevPage.data
      ) {
        await prevPage.mutate();
      }
      return false;
    }

    // If we need the next page's data but don't have it yet, we need to wait
    if (
      currentPositionInPage === pageSize - 1 &&
      shouldFetchNextPage &&
      !nextPage.data
    ) {
      await nextPage.mutate();
    }

    const getNextRunData = () => {
      if (currentPositionInPage === pageSize - 1) {
        // Only use next page data if we're supposed to have it
        if (shouldFetchNextPage && nextPage.data?.[0]) {
          return nextPage.data[0];
        }
        return undefined;
      } else {
        return currPage.data?.[currentPositionInPage + 1];
      }
    };

    const nextRunData = getNextRunData();
    if (!nextRunData) {
      // If we don't have the next run data when we should,
      // we need to block on the mutation
      return true;
    }

    // Optimistically update current page
    currPage.mutate(
      (currentData) => {
        if (!currentData) return currentData;
        const newData = [...currentData];

        // Shift everything back by one from current position
        for (let i = currentPositionInPage; i < pageSize - 1; i++) {
          newData[i] = newData[i + 1];
        }
        // Last position gets filled with next page data if appropriate
        if (shouldFetchNextPage && nextPage.data?.[0]) {
          newData[pageSize - 1] = nextPage.data[0];
        } else {
          // Otherwise mark it as undefined
          newData[pageSize - 1] =
            undefined as unknown as AnnotationQueueRunSchema; // Hack to make TS happy. We don't want to fill in a value here if we don't have it.
        }

        return newData;
      },
      { revalidate: false }
    );

    // If we're using next page data, update that optimistically too
    if (shouldFetchNextPage && nextPage.data) {
      nextPage.mutate(
        (nextData) => {
          if (!nextData) return nextData;
          const newData = [...nextData];
          // Shift everything back by one
          for (let i = 0; i < pageSize - 1; i++) {
            newData[i] = newData[i + 1];
          }
          // Last item becomes undefined since we don't know what it is
          newData[pageSize - 1] =
            undefined as unknown as AnnotationQueueRunSchema; // Hack to make TS happy. We don't want to fill in a value here if we don't have it.
          return newData;
        },
        { revalidate: false }
      );
    }
    return false;
  }, [
    index,
    pageSize,
    queue_size,
    nextPage,
    currPage,
    previousPageOffset,
    prevPage,
  ]);

  return {
    data: currPage.data?.[currentPositionInPage],
    goToNextRun,
    isLoading: currPage.isLoading,
    error: currPage.error,
  };
};
